import { useId, useState } from "react";
import { Mail, KeyRound, Cloud } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { Toggle } from "./Toggle";
import { ProviderCard } from "./ProviderCard";
import { EmailDomainsField } from "./EmailDomainsField";
import { EnforceSSOConfirmDialog } from "./EnforceSSOConfirmDialog";
import { useSSOForm } from "../hooks/useSSOForm";
import type { SSOFormState } from "../types";

interface SSOFormProps {
  organizationId: string;
  initial: SSOFormState;
  onSubmit: (input: ReturnType<ReturnType<typeof useSSOForm>["toMutationInput"]>) => Promise<void>;
  isSaving: boolean;
  serverError?: string;
  /** Per-provider feature flags from VITE_OIDC_PROVIDER_*_ENABLED. */
  providerFlags: { google: boolean; microsoft: boolean };
}

export function SSOForm({
  organizationId,
  initial,
  onSubmit,
  isSaving,
  serverError,
  providerFlags,
}: SSOFormProps) {
  const { t } = useLocale();
  const { state, errors, isDirty, isValid, dispatch, toMutationInput } =
    useSSOForm(initial);
  const [enforceDialogOpen, setEnforceDialogOpen] = useState(false);

  const enabledId = useId();
  const enforcedId = useId();
  const tenantId = useId();
  const tenantErrorId = `${tenantId}-error`;
  const hostedDomainId = useId();
  const hostedDomainErrorId = `${hostedDomainId}-error`;
  const formErrorId = useId();

  const submit = async () => {
    if (!isValid || !isDirty || isSaving) return;
    await onSubmit(toMutationInput(organizationId));
  };

  const handleSubmitClick = () => {
    // Surface a confirmation only when transitioning OFF→ON for `enforced`.
    const wasEnforced = initial.enforced;
    if (!wasEnforced && state.enforced) {
      setEnforceDialogOpen(true);
      return;
    }
    void submit();
  };

  const canSave = isValid && isDirty && !isSaving;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmitClick();
      }}
      className="space-y-6"
      noValidate
    >
      {/* ── Master toggles ─────────────────────────────────────────────── */}
      <section className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label
              htmlFor={enabledId}
              className="text-sm font-medium text-foreground"
            >
              {t("sso.field.enabled")}
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("sso.field.enabledHelp")}
            </p>
          </div>
          <Toggle
            id={enabledId}
            checked={state.enabled}
            onChange={(value) => dispatch({ type: "SET_ENABLED", value })}
            label={t("sso.field.enabled")}
          />
        </div>

        <div className="mt-5 flex items-start justify-between gap-4 border-t border-border pt-5">
          <div className="flex-1">
            <label
              htmlFor={enforcedId}
              className="text-sm font-medium text-foreground"
            >
              {t("sso.field.enforced")}
            </label>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("sso.field.enforcedHelp")}
            </p>
          </div>
          <Toggle
            id={enforcedId}
            checked={state.enforced}
            onChange={(value) => dispatch({ type: "SET_ENFORCED", value })}
            disabled={!state.enabled}
            label={t("sso.field.enforced")}
          />
        </div>
      </section>

      {/* ── Verified email domains ─────────────────────────────────────── */}
      <section className="rounded-lg border border-border bg-card p-5">
        <EmailDomainsField
          domains={state.emailDomains}
          onAdd={(value) => dispatch({ type: "ADD_EMAIL_DOMAIN", value })}
          onRemove={(index) =>
            dispatch({ type: "REMOVE_EMAIL_DOMAIN", index })
          }
          disabled={!state.enabled}
          errorKey={errors.emailDomains}
        />
      </section>

      {/* ── Identity providers ─────────────────────────────────────────── */}
      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t("sso.providers.heading")}
        </h2>

        <div className="space-y-4">
          {providerFlags.microsoft && (
            <ProviderCard
              icon={<Cloud size={18} />}
              title={t("sso.providers.microsoft.title")}
              description={t("sso.providers.microsoft.description")}
              enabled={state.microsoft.enabled}
              onEnabledChange={(value) =>
                dispatch({ type: "SET_MICROSOFT_ENABLED", value })
              }
              disabled={!state.enabled}
            >
              <div>
                <label
                  htmlFor={tenantId}
                  className="block text-sm font-medium text-foreground"
                >
                  {t("sso.providers.microsoft.tenantId")}
                </label>
                <input
                  id={tenantId}
                  type="text"
                  inputMode="text"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={state.microsoft.tenantId}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_MICROSOFT_TENANT_ID",
                      value: e.target.value,
                    })
                  }
                  placeholder="00000000-0000-4000-8000-000000000000"
                  aria-invalid={!!errors.microsoftTenantId}
                  aria-describedby={
                    errors.microsoftTenantId ? tenantErrorId : undefined
                  }
                  className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("sso.providers.microsoft.tenantIdHelp")}
                </p>
                {errors.microsoftTenantId && (
                  <p
                    id={tenantErrorId}
                    className="mt-1 text-xs text-destructive"
                  >
                    {t(`sso.errors.${errors.microsoftTenantId}`)}
                  </p>
                )}
              </div>
              <AllowJITToggle
                checked={state.microsoft.allowJIT}
                onChange={(value) =>
                  dispatch({ type: "SET_MICROSOFT_ALLOW_JIT", value })
                }
              />
            </ProviderCard>
          )}

          {providerFlags.google && (
            <ProviderCard
              icon={<Mail size={18} />}
              title={t("sso.providers.google.title")}
              description={t("sso.providers.google.description")}
              enabled={state.google.enabled}
              onEnabledChange={(value) =>
                dispatch({ type: "SET_GOOGLE_ENABLED", value })
              }
              disabled={!state.enabled}
            >
              <div>
                <label
                  htmlFor={hostedDomainId}
                  className="block text-sm font-medium text-foreground"
                >
                  {t("sso.providers.google.hostedDomain")}
                </label>
                <input
                  id={hostedDomainId}
                  type="text"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={state.google.hostedDomain}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_GOOGLE_HOSTED_DOMAIN",
                      value: e.target.value,
                    })
                  }
                  placeholder="acme.com"
                  aria-invalid={!!errors.googleHostedDomain}
                  aria-describedby={
                    errors.googleHostedDomain ? hostedDomainErrorId : undefined
                  }
                  className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("sso.providers.google.hostedDomainHelp")}
                </p>
                {errors.googleHostedDomain && (
                  <p
                    id={hostedDomainErrorId}
                    className="mt-1 text-xs text-destructive"
                  >
                    {t(`sso.errors.${errors.googleHostedDomain}`)}
                  </p>
                )}
              </div>
              <AllowJITToggle
                checked={state.google.allowJIT}
                onChange={(value) =>
                  dispatch({ type: "SET_GOOGLE_ALLOW_JIT", value })
                }
              />
            </ProviderCard>
          )}
        </div>
      </section>

      {/* ── Form-level errors + submit ──────────────────────────────────── */}
      <div className="flex items-center justify-between gap-4">
        <div id={formErrorId} className="flex-1 text-xs text-destructive">
          {errors.form && <span>{t(`sso.errors.${errors.form}`)}</span>}
          {serverError && <span>{serverError}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              dispatch({ type: "RESET", payload: initial })
            }
            disabled={!isDirty || isSaving}
            className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!canSave}
            aria-describedby={
              errors.form || serverError ? formErrorId : undefined
            }
            className="inline-flex h-9 items-center gap-1 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <KeyRound size={14} />
            {isSaving ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>

      {enforceDialogOpen && (
        <EnforceSSOConfirmDialog
          domains={state.emailDomains}
          onConfirm={() => {
            setEnforceDialogOpen(false);
            void submit();
          }}
          onCancel={() => setEnforceDialogOpen(false)}
        />
      )}
    </form>
  );
}

function AllowJITToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  const { t } = useLocale();
  const id = useId();
  const helpId = `${id}-help`;
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {t("sso.providers.allowJIT")}
        </label>
        <p id={helpId} className="mt-1 text-xs text-muted-foreground">
          {t("sso.providers.allowJITHelp")}
        </p>
      </div>
      <Toggle
        id={id}
        checked={checked}
        onChange={onChange}
        label={t("sso.providers.allowJIT")}
        describedBy={helpId}
      />
    </div>
  );
}
